<template>
  <div class="min-h-screen bg-gray-50 px-4 pt-6 pb-28" dir="rtl">
    <div class="max-w-4xl mx-auto md:max-w-lg">
      <!-- Alert -->
      <div class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-sm text-red-800">
          قبل از سفارش قطعه، برای درخواست وضعیت (اعلام هزینه به مشتری) و همچنین وضعیت (در انتظار قطعه) را ثبت نمایید
        </p>
      </div>

      <!-- Request List -->
      <div class="space-y-4">
        <div v-for="item in request_items" :key="item.Id" class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
          <!-- Header Section with Gradient -->
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div class="w-full">
                  <h3 class="text-base font-bold text-gray-900 ">درخواست کالا</h3>
                  <p class="text-xs text-gray-500 mt-0.5">{{ item.SubmitTime }}</p>
                </div>
              </div>
              <div class="px-3 py-1.5 bg-purple-100 text-purple-500 text-xs rounded-full">
                {{ item.StatusTitle || 'در انتظار قطعه' }}
              </div>
            </div>
          </div>

          <!-- Request Details -->
          <div class="px-5 py-4 space-y-3">
            <!-- Branch and Category Row -->
            <div class="grid grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-3">
                <p class="text-xs text-gray-500 mb-1.5">شعبه</p>
                <p class="text-sm font-semibold text-gray-900">{{ item.BranchName }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-3">
                <p class="text-xs text-gray-500 mb-1.5">دسته‌بندی</p>
                <p class="text-sm font-semibold text-gray-900">{{ item.CatTitle }}</p>
              </div>
            </div>

            <!-- Cost Center -->
            <div class="bg-gray-50 rounded-lg p-3">
              <p class="text-xs text-gray-500 mb-1.5">مرکز هزینه</p>
              <p class="text-sm font-semibold text-gray-900 leading-relaxed">{{ item.CostCenterName }}</p>
            </div>

            <!-- Description and Status (if exists) -->
            <div v-if="item.Summary" class="bg-gray-50 rounded-lg p-3">
              <p class="text-xs text-gray-500 mb-1.5">توضیحات</p>
              <p class="text-sm text-gray-700 leading-relaxed">{{ item.Summary }}</p>
            </div>
          </div>

          <!-- Divider -->
          <div class="px-5">
            <hr class="border-gray-200">
          </div>

          <!-- Products Section -->
          <div class="px-5 py-4 ">
            <div class="space-y-4">
              <div v-for="pro in item.Products" :key="pro.Id" class="">
                <!-- Product Name and Quantity -->
                <div class="flex items-start justify-between mb-4">
                  <div class="flex-1 bg-gray-50 rounded-lg p-3">
                    <p class="text-xs text-gray-500 mb-1.5">نام محصول</p>
                    <p class="text-sm font-bold text-gray-900 leading-relaxed">{{ pro.ProductName }}</p>
                  </div>
                  <div class="mr-4 flex-shrink-0 ">
                    <div class="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <p class="text-xs text-blue-600 mb-0.5">تعداد</p>
                      <p class="text-sm font-bold text-blue-700">{{ pro.Qty }} عدد</p>
                    </div>
                  </div>
                </div>

                <!-- Prices -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p class="text-xs text-green-700 mb-1.5 font-medium">قیمت خرید</p>
                    <p class="text-sm font-bold text-green-800">{{ formatPrice(pro.PurchaseAmount) }} تومان</p>
                  </div>
                  <div class="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p class="text-xs text-blue-700 mb-1.5 font-medium">قیمت فروش</p>
                    <p class="text-sm font-bold text-blue-800">{{ formatPrice(pro.SaleAmount) }} تومان</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Button -->
      <div class="fixed bottom-24 left-0 right-0 px-4 flex justify-center z-10">
        <button 
          class="w-full max-w-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 font-semibold rounded-lg transition-all duration-200"
          @click="dialog_factor = true"
        >
          {{ $t('add_product_request') }}
        </button>
      </div>

      <infinite-loading @infinite="infiniteHandler" v-if="show_loading">
        <div slot="no-more">{{ $t('no_record') }}</div>
      </infinite-loading>
    </div>

    <!-- Dialog Modal -->
    <div v-if="dialog_factor" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-center justify-center">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-90vh overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-bold text-gray-900">{{ $t('add_product_request') }}</h2>
        </div>

        <!-- Body -->
        <div class="px-6 py-4 overflow-y-auto flex-1">
          <!-- Category Select -->
          <div v-if="$route.params.rec_id==0" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('category') }}</label>
            <div class="relative">
              <select 
                v-model="selected_category" 
                class="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer"
              >
                <option value="0" disabled>{{ $t('category') }}</option>
                <option v-for="item in category_items" :key="item.value" :value="item.value">
                  {{ item.label }}
                </option>
              </select>
              <div class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Branch Select -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('branch') }}</label>
            <div class="relative">
              <select 
                v-model="selected_branch" 
                class="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer"
              >
                <option value="0" disabled>{{ $t('branch') }}</option>
                <option v-for="item in post_items" :key="item.value" :value="item.value">
                  {{ item.label }}
                </option>
              </select>
              <div class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Request Select -->
          <div v-if="selected_category==3" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('search_request') }}</label>
            <treeselect 
              :multiple="false"
              :load-options="load_requests"
              :normalizer="normalizer_requests"
              :async="true"
              :placeholder="$t('search_request')"
              v-model="selected_request"
            />
          </div>

          <!-- User Select -->
          <div v-if="selected_category==4" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('search_user') }}</label>
            <treeselect 
              :multiple="false"
              :load-options="load_persons"
              :normalizer="normalizer_persons"
              :async="true"
              :placeholder="$t('search_user')"
              v-model="selected_user"
            />
          </div>

          <!-- Summary -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('summary') }}</label>
            <textarea 
              v-model="request_summary" 
              rows="3"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              :placeholder="$t('summary')"
            ></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button 
            class="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            @click="dialog_factor = false"
          >
            {{ $t('cancel') }}
          </button>
          <button 
            class="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200"
            @click="goToItems()"
          >
            {{ $t('ok') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import Treeselect, { ASYNC_SEARCH } from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { bus } from '../../main'

const simulateAsyncOperation = fn => {
  setTimeout(fn, 200)
}

export default {
  props: [
    'passing_data'
  ],

  data() {
    return {
      list: [],
      errors: [],
      post_items: [],
      selected_post: 0,
      dialog_factor: false,
      selected_branch: 0,
      request_items: [],
      last_request_items: [],
      show_loading: false,
      page: 0,
      request_summary: '',
      selected_request: null,
      selected_user: null,
      category_items: [],
      selected_category: 0,
      normalizer_requests(node) {
        return {
          id: node.Id,
          label: node.Title,
        }
      },
      normalizer_persons(node) {
        return {
          id: node.Id,
          label: node.UserName,
        }
      },
      first: true,
    }
  },
  components: {
    InfiniteLoading,
    Treeselect
  },
  watch: {
    selected_category: function (val) {
      this.selected_request = null
      this.selected_user = null
    }
  },
  mounted() {
    this.get_pre_requests()
    this.get_categories()
    this.get_posts()
  },

  methods: {
    load_requests({ action, searchQuery, callback }) {
      this.model_txt = searchQuery
      var self = this
      if (action === ASYNC_SEARCH) {
        simulateAsyncOperation(() => {
          let options

          var user_id = JSON.parse(localStorage.getItem('profile')).UserId
          const send_data = {
            Txt: searchQuery,
            ExpertId: user_id,
            BranchId: self.selected_branch,
          }
          this.axios.post('/GetUserDevicesByTxt', send_data, {})
            .then(response => {
              self.search_entries = response.data.results
              options = response.data.data
              callback(null, options)
            }).catch(e => {
              this.errors.push(e)
            })
        })
      }
    },

    load_persons({ action, searchQuery, callback }) {
      this.model_txt = searchQuery
      var self = this
      if (action === ASYNC_SEARCH) {
        simulateAsyncOperation(() => {
          let options
          var user_id = JSON.parse(localStorage.getItem('profile')).UserId

          const send_data = {
            Txt: searchQuery,
            UserId: user_id
          }
          this.axios.post('/GetUsersByTxt', send_data, {})
            .then(response => {
              options = response.data.Payks
              callback(null, options)
            }).catch(e => {
              this.errors.push(e)
            })
        })
      }
    },

    get_posts() {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      console.log()
      const send_data = {
        UserId: user_id,
      }
      this.axios.post('/GetBranches', send_data, {})
        .then(response => {
          if (response.data.error == 0) {
            response.data.branches.forEach(function (number) {
              var obj = { label: number.BranchName, value: number.Id }
              self.post_items.push(obj)
            })
            self.get_branch_by_post()
          }
        }).catch(e => {
          this.errors.push(e)
        })
    },
    get_branch_by_post() {
      var self = this
      const send_data = {
        PartyId: localStorage.getItem('party_id'),
      }
      this.axios.post('/GetBranchIdByPartyId', send_data, {})
        .then(response => {
          if (response.data.error == 0) {
            self.selected_branch = response.data.branchId
          }
        }).catch(e => {
          this.errors.push(e)
        })
    },

    goToDetail(item) {
      var passing_data = {
        category: this.selected_category,
        user: this.selected_user,
        request: this.selected_request,
        branch: this.selected_branch,
        summary: this.request_summary,
      }
      localStorage.setItem("request_product", JSON.stringify(passing_data));
      console.log(passing_data)
      this.dialog_factor = false
      this.$router.push({ name: 'ProductRequestFactorItems', params: { rec_id: item.Id } })
    },
    goToItems(item) {
      if (this.selected_branch == 0) {
        bus.$emit('show_toast', [1500, this.$t('please_select_branch')])
        return false
      }
      if (this.selected_category == 0 && this.$route.params.rec_id == 0) {
        bus.$emit('show_toast', [1500, this.$t('select_category')])
        return false
      }
      if (this.selected_category == 3 && this.selected_request == null) {
        bus.$emit('show_toast', [1500, this.$t('select_request')])
        return false
      }
      if (this.selected_category == 4 && this.selected_user == null) {
        bus.$emit('show_toast', [1500, this.$t('select_user')])
        return false
      }
      var passing_data;
      if (this.$route.params.rec_id == 0) {
        passing_data = {
          category: this.selected_category,
          user: this.selected_user,
          request: this.selected_request,
          branch: this.selected_branch,
          summary: this.request_summary,
        }
      } else {
        passing_data = {
          category: 3,
          user: this.selected_user,
          request: this.$route.params.rec_id,
          branch: this.selected_branch,
          summary: this.request_summary,
        }
      }

      localStorage.setItem("request_product", JSON.stringify(passing_data));
      console.log(passing_data)
      this.dialog_factor = false
      this.$router.push({ name: 'ProductRequestFactorItems', params: { rec_id: 0 } })
    },
    get_pre_requests($state) {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      const send_data = {
        UserId: user_id,
        Count: this.page,
        CostCenterId: this.$route.params.rec_id,
      }
      console.log('pre request called')
      this.axios.post('/GetGoodsReqByReqId', send_data, {})
        .then(response => {
          self.request_items = []
          if (response.data.error == 0) {
            response.data.items.forEach(function (number) {
              self.request_items.push(number)
            })
          }
        }).catch(e => {
          this.errors.push(e)
        })
    },
    get_categories() {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      const send_data = {
        UserId: user_id,
        Count: this.page,
        CostCenterId: 0,
      }
      console.log('pre request called')
      this.axios.post('/GetProductRequestCategories', send_data, {})
        .then(response => {
          self.category_items = []
          if (response.data.error == 0) {
            response.data.items.forEach(function (number) {
              console.log(number)
              var obj = { label: number.RecTitle, value: number.RecId }
              self.category_items.push(obj)
            })
          }
        }).catch(e => {
          this.errors.push(e)
        })
    },
    infiniteHandler($state) {
      var self = this
      if (this.first) {
        setTimeout(() => {
          $state.loaded()
        }, 1000)
        return false
      }

      console.log('hiii' + self.last_request_items.length)
      setTimeout(() => {
        self.page += 1
        self.get_pre_requests($state)
      }, 1000)
    },
    formatPrice(price) {
      if (!price) return '0'
      // Convert to string and add thousand separators
      var priceStr = String(price)
      return priceStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
  }
}
</script>

<style scoped>
/* Custom styles for Treeselect to match design */
.vue-treeselect__control {
  border-color: #d1d5db !important;
  border-radius: 0.5rem !important;
}

.vue-treeselect__control:hover {
  border-color: #3b82f6 !important;
}

.vue-treeselect__control:focus {
  border-color: #3b82f6 !important;
  /* box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important; */
}

/* Ensure selects have proper styling */
select {
  border: 1px solid #d1d5db !important;
  border-radius: 0.5rem !important;
}

select:focus {
  outline: none;
  border-color: #3b82f6 !important;
  /* box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important; */
}

/* Ensure textarea has proper styling */
textarea {
  border: 1px solid #d1d5db !important;
  border-radius: 0.5rem !important;
}

textarea:focus {
  outline: none;
  border-color: #3b82f6 !important;
  /* box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important; */
}
</style>